/**
 * Settings - Quality, Audio, and Subtitle settings drawer
 */
import React, { useState, useEffect, memo } from 'react';
import { useFocusable, FocusContext } from '@noriginmedia/norigin-spatial-navigation';
import { usePlayer, usePlayerState } from '../core/PlayerContext';
import { useInputMode } from '../../utils/hooks/useInputMode';

// Persian language labels - synced with Android TV's labelSwitchGuide
var languageLabels = {
    // Off
    off: 'خاموش',
    original: 'پیشفرض',

    // Persian (فارسی)
    persian: 'فارسی',
    fa: 'فارسی',
    'fa-IR': 'فارسی',

    // English (انگلیسی)
    english: 'انگلیسی',
    en: 'انگلیسی',
    'en-US': 'انگلیسی',

    // Spanish (اسپانیایی)
    spanish: 'اسپانیایی',
    es: 'اسپانیایی',
    'es-ES': 'اسپانیایی',

    // French (فرانسوی)
    french: 'فرانسوی',
    fr: 'فرانسوی',
    'fr-FR': 'فرانسوی',

    // German (آلمانی)
    german: 'آلمانی',
    de: 'آلمانی',
    'de-DE': 'آلمانی',

    // Chinese (چینی)
    chinese: 'چینی',
    zh: 'چینی',
    'zh-CN': 'چینی',

    // Arabic (عربی)
    arabic: 'عربی',
    ar: 'عربی',
    'ar-SA': 'عربی',

    // Russian (روسی)
    russian: 'روسی',
    ru: 'روسی',
    'ru-RU': 'روسی',

    // Hindi (هندی)
    hindi: 'هندی',
    hi: 'هندی',
    'hi-IN': 'هندی',

    // Japanese (ژاپنی)
    japanese: 'ژاپنی',
    ja: 'ژاپنی',
    'ja-JP': 'ژاپنی',

    // Portuguese (پرتغالی)
    portuguese: 'پرتغالی',
    pt: 'پرتغالی',
    'pt-PT': 'پرتغالی',

    // Italian (ایتالیایی)
    italian: 'ایتالیایی',
    it: 'ایتالیایی',
    'it-IT': 'ایتالیایی',

    // Korean (کره‌ای)
    korean: 'کره‌ای',
    ko: 'کره‌ای',
    'ko-KR': 'کره‌ای',

    // Turkish (ترکی)
    turkish: 'ترکی',
    tr: 'ترکی',
    'tr-TR': 'ترکی',
};

function getPersianLabel(label) {
    if (!label) return label;
    var key = label.toLowerCase();
    return languageLabels[key] || label;
}

/**
 * Get quality label (translate Auto to Persian)
 */
function getQualityLabel(label) {
    if (!label) return label;
    if (label === 'Auto' || label === 'auto') return 'خودکار';
    return label;
}

/**
 * Settings Option Button
 */
var SettingsOption = memo(function SettingsOption(props) {
    var label = props.label;
    var selected = props.selected;
    var onSelect = props.onSelect;
    var focusKey = props.focusKey;

    var { focused, ref, focusSelf } = useFocusable({
        focusKey: focusKey,
        onEnterPress: onSelect,
    });

    // Input mode tracking for scroll behavior
    var inputMode = useInputMode();
    var isMouseFocus = inputMode.isMouseFocus;
    var setMouseFocus = inputMode.setMouseFocus;

    // Auto-scroll into view when focused (skip for mouse hover)
    useEffect(function () {
        if (focused && ref.current && !isMouseFocus) {
            ref.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    }, [focused, ref, isMouseFocus]);

    // Trigger focus on mouse hover
    var handleMouseEnter = function () {
        setMouseFocus(true);
        if (focusSelf) {
            focusSelf();
        }
    };

    return React.createElement('button', {
        ref: ref,
        className: 'mnb-settings-option ' +
            (selected ? 'selected ' : '') +
            (focused ? 'focused' : ''),
        onClick: onSelect,
        onMouseEnter: handleMouseEnter,
        type: 'button',
    },
        React.createElement('span', null, label),
        selected ? React.createElement('svg', {
            viewBox: '0 0 24 24',
            fill: 'currentColor',
            width: 24,
            height: 24
        },
            React.createElement('path', { d: 'M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z' })
        ) : null
    );
});

/**
 * Back Button Component for drawer header
 */
var BackButton = memo(function BackButton(props) {
    var onClose = props.onClose;
    var focusKeyProp = props.focusKey || 'back-btn';

    var { focused, ref, focusSelf } = useFocusable({
        focusKey: focusKeyProp,
        onEnterPress: onClose,
    });

    // Input mode tracking for scroll behavior
    var inputMode = useInputMode();
    var isMouseFocus = inputMode.isMouseFocus;
    var setMouseFocus = inputMode.setMouseFocus;

    // Auto-scroll into view when focused (skip for mouse hover)
    useEffect(function () {
        if (focused && ref.current && !isMouseFocus) {
            ref.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    }, [focused, ref, isMouseFocus]);

    // Trigger focus on mouse hover
    var handleMouseEnter = function () {
        setMouseFocus(true);
        if (focusSelf) focusSelf();
    };

    return React.createElement('button', {
        ref: ref,
        className: 'mnb-episode-back-btn ' + (focused ? 'focused' : ''),
        onClick: onClose,
        onMouseEnter: handleMouseEnter,
        type: 'button',
    },
        React.createElement('svg', {
            viewBox: '0 0 24 24',
            fill: 'currentColor',
            width: 56,
            height: 56,
        },
            React.createElement('path', { d: 'M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z' })
        ),
    );
});

/**
 * Quality Settings Panel
 */
var QualitySettings = function QualitySettings(props) {
    var onClose = props.onClose;
    var player = usePlayer();
    var qualities = usePlayerState('qualities');
    var currentQuality = usePlayerState('currentQuality');
    var audioTracks = usePlayerState('audioTracks');
    var currentAudioTrack = usePlayerState('currentAudioTrack');

    var { ref, focusKey, focusSelf } = useFocusable({
        focusKey: 'qualitySettings',
        isFocusBoundary: true,
        focusBoundaryDirections: ['right'],
        onArrowPress: function (direction) {
            // Close drawer on left arrow press
            if (direction === 'left') {
                onClose();
                return true;
            }
            // Allow default behavior for up/down navigation
            return false;
        },
    });

    // Transfer focus to drawer when it mounts
    useEffect(function () {
        // Small delay to ensure DOM is ready
        var timer = setTimeout(function () {
            focusSelf();
        }, 50);
        return function () { clearTimeout(timer); };
    }, [focusSelf]);

    return React.createElement(FocusContext.Provider, { value: focusKey },
        React.createElement('div', { className: 'mnb-settings-content', ref: ref },

            // Header with back button
            React.createElement('div', { className: 'mnb-settings-header' },
                React.createElement('h2', { className: 'mnb-settings-header-title' }, 'تنظیمات پخش'),
                React.createElement(BackButton, { onClose: onClose, focusKey: 'quality-back-btn' })
            ),

            // Video Quality Section (کیفیت ویدیو - matching Android TV)
            React.createElement('div', { className: 'mnb-settings-section' },
                React.createElement('h3', { className: 'mnb-settings-title' }, 'کیفیت ویدیو'),
                qualities.map(function (quality, index) {
                    return React.createElement(SettingsOption, {
                        key: 'quality-' + quality.index,
                        label: getQualityLabel(quality.label),
                        selected: currentQuality === quality.index,
                        focusKey: 'quality-option-' + index,
                        onSelect: function () {
                            player.setQuality(quality.index);
                        },
                    });
                })
            ),

            // Audio Tracks Section (کانال صوتی - matching Android TV)
            audioTracks.length > 1 ? React.createElement('div', { className: 'mnb-settings-section' },
                React.createElement('h3', { className: 'mnb-settings-title' }, 'کانال صوتی'),
                audioTracks.map(function (track, index) {
                    return React.createElement(SettingsOption, {
                        key: 'audio-' + track.index,
                        label: getPersianLabel(track.label),
                        selected: currentAudioTrack === track.index,
                        focusKey: 'audio-option-' + index,
                        onSelect: function () {
                            player.setAudioTrack(track.index);
                        },
                    });
                })
            ) : null
        )
    );
};

/**
 * Caption Settings Panel  
 * Matches Android TV's TvSubtitleSettingsDrawer features
 */
var CaptionSettings = function CaptionSettings(props) {
    var onClose = props.onClose;
    var player = usePlayer();
    var textTracks = usePlayerState('textTracks');
    var currentTextTrack = usePlayerState('currentTextTrack');

    // Load saved settings from localStorage (with fallbacks)
    var getSavedSetting = function (key, defaultValue) {
        try {
            var saved = localStorage.getItem('mnb-subtitle-' + key);
            if (saved !== null) {
                // Parse numbers, keep strings as-is
                var parsed = Number(saved);
                return isNaN(parsed) ? saved : parsed;
            }
        } catch (e) {
            // localStorage may fail (private browsing, etc.)
        }
        return defaultValue;
    };

    var saveSetting = function (key, value) {
        try {
            localStorage.setItem('mnb-subtitle-' + key, String(value));
        } catch (e) {
            // Ignore localStorage errors
        }
    };

    var [fontSize, setFontSizeState] = useState(function () { return getSavedSetting('fontSize', 1); });
    var [textColor, setTextColorState] = useState(function () { return getSavedSetting('textColor', '#ffffff'); });
    var [bgColor, setBgColorState] = useState(function () { return getSavedSetting('bgColor', 'rgba(0, 0, 0, 0.5)'); });

    // Wrappers that persist to localStorage
    var setFontSize = function (val) {
        setFontSizeState(val);
        saveSetting('fontSize', val);
    };
    var setTextColor = function (val) {
        setTextColorState(val);
        saveSetting('textColor', val);
    };
    var setBgColor = function (val) {
        setBgColorState(val);
        saveSetting('bgColor', val);
    };

    var { ref, focusKey, focusSelf } = useFocusable({
        focusKey: 'captionSettings',
        isFocusBoundary: true,
        focusBoundaryDirections: ['right'],
        onArrowPress: function (direction) {
            // Close drawer on left arrow press
            if (direction === 'left') {
                onClose();
                return true;
            }
            // Allow default behavior for up/down navigation
            return false;
        },
    });

    // Transfer focus to drawer when it mounts
    useEffect(function () {
        // Small delay to ensure DOM is ready
        var timer = setTimeout(function () {
            focusSelf();
        }, 50);
        return function () { clearTimeout(timer); };
    }, [focusSelf]);

    // Dynamically inject ::cue styles for subtitle customization
    // Note: ::cue pseudo-element doesn't support CSS variables, so we inject raw values
    // Also legacy Chromium (WebOS/Tizen) doesn't support CSS variables at all
    useEffect(function () {
        // Calculate font size in pixels (base is ~40px on TV for readability)
        var fontSizePx = Math.round(40 * fontSize);

        // Create or update the style element
        var styleId = 'mnb-cue-style';
        var style = document.getElementById(styleId);
        if (!style) {
            style = document.createElement('style');
            style.id = styleId;
            document.head.appendChild(style);
        }

        // Build the ::cue CSS rule
        // Note: ::cue has limited property support - color, background, font-size work in most browsers
        style.textContent =
            'video::cue {' +
            'color: ' + textColor + ' !important;' +
            'background-color: ' + bgColor + ' !important;' +
            'font-size: ' + fontSizePx + 'px !important;' +
            'font-family: \"AnjomanMax\", sans-serif !important;' +
            '}' +
            // WebKit-specific selector for older browsers
            'video::-webkit-media-text-track-display {' +
            'display: inline-block !important;' +
            'background-color: transparent !important;' +
            'font-size: ' + fontSizePx + 'px !important;' +
            'text-align: center !important;' +
            '}' +
            'video::-webkit-media-text-track-container {' +
            'font-size: ' + fontSizePx + 'px !important;' +
            'bottom: 10vh !important;' +
            'top: auto !important;' +
            'text-align: center !important;' +
            '}';

        console.log('[CaptionSettings] Updated cue styles - fontSize:', fontSizePx, 'color:', textColor, 'bg:', bgColor);

        // Cleanup on unmount
        return function () {
            // Don't remove the styles on unmount - keep them applied
            // Only remove if component is fully cleaned up (rare case)
        };
    }, [fontSize, textColor, bgColor]);

    // Color options matching Android TV
    var colorOptions = [
        { label: 'سفید', value: '#ffffff' },
        { label: 'مشکی', value: '#000000' },
        { label: 'زرد', value: '#ffff00' },
        { label: 'قرمز', value: '#ff0000' },
        { label: 'سبز', value: '#00ff00' },
        { label: 'آبی', value: '#0000ff' },
        { label: 'فیروزه‌ای', value: '#00ffff' },
    ];

    // Background options matching Android TV
    var bgOptions = [
        { label: 'شفاف', value: 'transparent' },
        { label: 'نیمه شفاف', value: 'rgba(0, 0, 0, 0.5)' },
        { label: 'مشکی کامل', value: 'rgba(0, 0, 0, 1)' },
    ];

    // Font size options matching Android TV
    var fontSizeOptions = [
        { label: 'خیلی کوچک', value: 0.6 },
        { label: 'کوچک', value: 0.8 },
        { label: 'متوسط', value: 1.0 },
        { label: 'بزرگ', value: 1.2 },
        { label: 'خیلی بزرگ', value: 1.4 },
        { label: 'بسیار بزرگ', value: 1.6 },
    ];

    return React.createElement(FocusContext.Provider, { value: focusKey },
        React.createElement('div', { className: 'mnb-settings-content', ref: ref },

            // Header with back button
            React.createElement('div', { className: 'mnb-settings-header' },
                React.createElement('h2', { className: 'mnb-settings-header-title' }, 'تنظیمات زیرنویس'),
                React.createElement(BackButton, { onClose: onClose, focusKey: 'caption-back-btn' })
            ),

            // Subtitle Language (زبان زیرنویس)
            React.createElement('div', { className: 'mnb-settings-section' },
                React.createElement('h3', { className: 'mnb-settings-title' }, 'زبان زیرنویس'),
                React.createElement(SettingsOption, {
                    label: 'خاموش',
                    selected: currentTextTrack === -1,
                    focusKey: 'caption-off',
                    onSelect: function () { player.disableTextTracks(); },
                }),
                textTracks.map(function (track, index) {
                    return React.createElement(SettingsOption, {
                        key: 'caption-' + track.index,
                        label: getPersianLabel(track.label),
                        selected: currentTextTrack === track.index,
                        focusKey: 'caption-option-' + index,
                        onSelect: function () {
                            player.setTextTrack(track.index);
                        },
                    });
                })
            ),

            // Text Color (رنگ متن - matching Android TV)
            React.createElement('div', { className: 'mnb-settings-section' },
                React.createElement('h3', { className: 'mnb-settings-title' }, 'رنگ متن'),
                colorOptions.map(function (option, index) {
                    return React.createElement(SettingsOption, {
                        key: 'color-' + index,
                        label: option.label,
                        selected: textColor === option.value,
                        focusKey: 'color-option-' + index,
                        onSelect: function () { setTextColor(option.value); },
                    });
                })
            ),

            // Font Size (اندازه فونت - matching Android TV)
            React.createElement('div', { className: 'mnb-settings-section' },
                React.createElement('h3', { className: 'mnb-settings-title' }, 'اندازه فونت'),
                fontSizeOptions.map(function (option, index) {
                    return React.createElement(SettingsOption, {
                        key: 'size-' + index,
                        label: option.label,
                        selected: Math.abs(fontSize - option.value) < 0.05,
                        focusKey: 'size-option-' + index,
                        onSelect: function () { setFontSize(option.value); },
                    });
                })
            ),

            // Background (پس‌زمینه زیرنویس - matching Android TV)
            React.createElement('div', { className: 'mnb-settings-section' },
                React.createElement('h3', { className: 'mnb-settings-title' }, 'پس‌زمینه زیرنویس'),
                bgOptions.map(function (option, index) {
                    return React.createElement(SettingsOption, {
                        key: 'bg-' + index,
                        label: option.label,
                        selected: bgColor === option.value,
                        focusKey: 'bg-option-' + index,
                        onSelect: function () { setBgColor(option.value); },
                    });
                })
            )
        )
    );
};

/**
 * Main Settings Drawer
 */
var Settings = function Settings(props) {
    var isOpen = props.isOpen;
    var onClose = props.onClose;
    var type = props.type || 'quality';
    var restoreFocusKey = props.restoreFocusKey;

    // Import setFocus for focus restoration
    var setFocus = require('@noriginmedia/norigin-spatial-navigation').setFocus;

    // Wrapper that closes drawer and restores focus
    var handleClose = function () {
        onClose();
        // Restore focus to the toggle button after a short delay
        if (restoreFocusKey) {
            setTimeout(function () {
                setFocus(restoreFocusKey);
            }, 100);
        }
    };

    // Track if drawer should be visible (delayed state for animation)
    var [showDrawer, setShowDrawer] = useState(false);
    var [isAnimating, setIsAnimating] = useState(false);

    // Handle open/close with animation
    useEffect(function () {
        if (isOpen) {
            // First, show the drawer (add to DOM)
            setShowDrawer(true);
            // Then, after a tiny delay, trigger the animation
            var timer = setTimeout(function () {
                setIsAnimating(true);
            }, 10);
            return function () { clearTimeout(timer); };
        } else {
            // First, start closing animation
            setIsAnimating(false);
            // Then, after animation completes, remove from DOM
            var timer = setTimeout(function () {
                setShowDrawer(false);
            }, 200);
            return function () { clearTimeout(timer); };
        }
    }, [isOpen]);

    // Prevent scroll when open
    useEffect(function () {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return function () {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    // Handle keyboard events to close drawer (Escape, Backspace, Back, Left Arrow)
    useEffect(function () {
        if (!isOpen) return;

        var handleKeyDown = function (e) {
            // Escape key, Backspace, or XF86Back (TV back button)
            if (e.key === 'Escape' || e.keyCode === 27 ||
                e.key === 'Backspace' || e.keyCode === 8 ||
                e.key === 'XF86Back' || e.keyCode === 10009 ||  // Tizen back
                e.keyCode === 461 ||  // LG WebOS back
                e.key === 'ArrowLeft' || e.keyCode === 37) {  // Left arrow closes drawer
                e.preventDefault();
                e.stopPropagation();
                handleClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown, true);
        return function () {
            window.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [isOpen, handleClose]);

    if (!showDrawer) return null;

    return React.createElement(React.Fragment, null,
        // Backdrop
        React.createElement('div', {
            className: 'mnb-settings-backdrop ' + (isAnimating ? 'visible' : ''),
            onClick: handleClose,
        }),

        // Drawer
        React.createElement('div', { className: 'mnb-settings-drawer ' + (isAnimating ? 'open' : '') },
            type === 'quality'
                ? React.createElement(QualitySettings, { onClose: handleClose })
                : React.createElement(CaptionSettings, { onClose: handleClose })
        )
    );
};

export { Settings, QualitySettings, CaptionSettings, SettingsOption };
